import React, { useState, useEffect } from "react";
import { BrandGuideItem } from "./api";
import {
  useCreateBrandGuideItem,
  useCreateBrandGuideItemWithFile,
  useUpdateBrandGuideItem,
  useUpdateBrandGuideItemWithFile,
} from "./hooks";
import { useT } from "../../i18n";

interface BrandGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingItem?: BrandGuideItem | null;
}

const BrandGuideModal: React.FC<BrandGuideModalProps> = ({
  isOpen,
  onClose,
  editingItem,
}) => {
  const t = useT();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Logos & Brand Marks");
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  const createMutation = useCreateBrandGuideItem();
  const createWithFileMutation = useCreateBrandGuideItemWithFile();
  const updateMutation = useUpdateBrandGuideItem();
  const updateWithFileMutation = useUpdateBrandGuideItemWithFile();

  const isEditMode = !!editingItem;

  // Function to determine icon type and color based on file
  const getFileDisplayInfo = (filename: string) => {
    const extension = filename.toLowerCase().split(".").pop();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"];

    if (imageExtensions.includes(extension || "")) {
      return { iconType: "file-image", iconColor: "blue" };
    }

    switch (extension) {
      case "pdf":
        return { iconType: "file-pdf", iconColor: "red" };
      case "doc":
      case "docx":
        return { iconType: "file-word", iconColor: "blue" };
      case "ppt":
      case "pptx":
        return { iconType: "file-powerpoint", iconColor: "orange" };
      case "xls":
      case "xlsx":
        return { iconType: "file-excel", iconColor: "green" };
      case "zip":
      case "rar":
        return { iconType: "file-zipper", iconColor: "purple" };
      default:
        return { iconType: "file", iconColor: "gray" };
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && editingItem) {
      setTitle(editingItem.title);
      setDescription(editingItem.description);
      setCategory(editingItem.category);
      setFileKey(editingItem.fileKey || "");
      setIsActive(editingItem.isActive);
      setSortOrder(editingItem.sortOrder);
    } else if (!isEditMode) {
      resetForm();
    }
  }, [editingItem, isEditMode, isOpen]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("Logos & Brand Marks");
    setFile(null);
    setFileKey("");
    setIsActive(true);
    setSortOrder(0);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Determine icon type and color based on file
    let iconType = "file";
    let iconColor = "gray";

    if (file) {
      const fileInfo = getFileDisplayInfo(file.name);
      iconType = fileInfo.iconType;
      iconColor = fileInfo.iconColor;
    } else if (fileKey) {
      const fileInfo = getFileDisplayInfo(fileKey);
      iconType = fileInfo.iconType;
      iconColor = fileInfo.iconColor;
    }

    const formData = {
      title,
      description,
      category,
      iconType,
      iconColor,
      fileKey,
      isActive,
      sortOrder,
    };

    try {
      if (isEditMode && editingItem) {
        if (file) {
          await updateWithFileMutation.mutateAsync({
            id: editingItem.id,
            data: formData,
            file,
          });
        } else {
          await updateMutation.mutateAsync({
            id: editingItem.id,
            data: formData,
          });
        }
      } else {
        if (file) {
          await createWithFileMutation.mutateAsync({
            data: formData,
            file,
          });
        } else {
          await createMutation.mutateAsync(formData);
        }
      }

      onClose();
      resetForm();
    } catch (error) {
      console.error("Error saving brand guide item:", error);
    }
  };

  const categoryOptions = [
    { value: "Logos & Brand Marks", label: "Logos & Brand Marks" },
    { value: "Usage Guidelines", label: "Usage Guidelines" },
    { value: "Templates & Assets", label: "Templates & Assets" },
  ];

  const iconOptions = [
    { value: "file-image", label: "Image File" },
    { value: "file-pdf", label: "PDF File" },
    { value: "file-powerpoint", label: "PowerPoint File" },
    { value: "file-word", label: "Word Document" },
    { value: "file-excel", label: "Excel File" },
    { value: "file-zip", label: "Archive File" },
    { value: "palette", label: "Design Asset" },
    { value: "image", label: "Image" },
    { value: "video", label: "Video" },
  ];

  const colorOptions = [
    { value: "blue", label: "Blue" },
    { value: "green", label: "Green" },
    { value: "purple", label: "Purple" },
    { value: "orange", label: "Orange" },
    { value: "red", label: "Red" },
    { value: "gray", label: "Gray" },
  ];

  if (!isOpen) return null;

  const isLoading =
    createMutation.isPending ||
    createWithFileMutation.isPending ||
    updateMutation.isPending ||
    updateWithFileMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditMode
              ? t("brandGuide.modal.editTitle")
              : t("brandGuide.modal.createTitle")}
          </h2>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("brandGuide.modal.titleLabel")}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t("brandGuide.modal.titlePlaceholder")}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("brandGuide.modal.descriptionLabel")}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t("brandGuide.modal.descriptionPlaceholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("brandGuide.modal.categoryLabel")}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categoryOptions.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("brandGuide.modal.sortOrderLabel")}
              </label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("brandGuide.modal.fileUploadLabel")}
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.jpg,.jpeg,.png,.svg,.eps"
              />
              {!file && fileKey && (
                <p className="text-sm text-gray-500 mt-1">
                  {t("common.currentFile", { name: fileKey.split("/").pop() || "" })}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("brandGuide.modal.fileKeyLabel")}
              </label>
              <input
                type="text"
                value={fileKey}
                onChange={(e) => setFileKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t("brandGuide.modal.fileKeyPlaceholder")}
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  {t("brandGuide.modal.activeLabel")}
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  {isEditMode
                    ? t("common.loading.updating")
                    : t("common.loading.creating")}
                </>
              ) : (
                <>
                  <i
                    className={`fa-solid ${
                      isEditMode ? "fa-save" : "fa-plus"
                    } mr-2`}
                  ></i>
                  {isEditMode
                    ? t("brandGuide.modal.submitUpdate")
                    : t("brandGuide.modal.submitCreate")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandGuideModal;
